import {
  AUDIO_MEDIA_FORMATS,
  CURATED_IMPACT_V1,
  audioBundleFilename,
  type AudioMediaKind,
  type AudioSelection,
  type CuratedSfxSpec,
  type PublishedAudioAsset,
} from '@djgg/core';
import type { AudioEngine } from './engine.js';
import { curatedImpactWav } from './pcmSfx.js';
import { bankEntry, bankSampleId, renderBankEntry } from './sfxBank.js';
import type { AudioLoadStats } from './types.js';
export { CURATED_IMPACT_V1, type CuratedSfxSpec } from '@djgg/core';

type LoadState = 'pending' | 'loaded' | 'failed';
type FetchAudio = typeof fetch;

function isCueName(value: string): boolean { return /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/.test(value); }
function isSelection(value: unknown): value is Exclude<AudioSelection, 'none'> {
  // v1 callers historically pass CURATED_IMPACT_V1 itself (which carries
  // descriptive fields beyond the three selection-identity fields).
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const selection = value as { variantId?: unknown; contentSha256?: unknown; source?: unknown };
  if (typeof selection.variantId !== 'string' || typeof selection.contentSha256 !== 'string' ||
    !/^[a-f0-9]{64}$/.test(selection.contentSha256)) return false;
  if (selection.source === 'bank-sfx-v1') return /^[a-z0-9_-]+\.(arcade|soft)\.v[12]$/.test(selection.variantId);
  return (selection.source === 'curated-impact-v1' || selection.source === 'uploaded-audio-v1') &&
    /^[A-Za-z0-9][A-Za-z0-9_-]{0,99}$/.test(selection.variantId);
}
/** The version-local URL the publisher wrote. The filename comes from
 *  `@djgg/core` so the publisher and the runtime cannot disagree about it —
 *  compressed music is `.ogg`, an uploaded file `.wav`. */
function relativeAudioUrl(asset: PublishedAudioAsset): string {
  return `./djgg-assets/${audioBundleFilename(asset.contentSha256, asset.audio.mime)}`;
}

/** A manifest-owned binding. Returns handled even for unavailable declarations
 * so a failed/rejected selection never substitutes a legacy recipe. No URLs. */
export function bindCuratedSfx(
  engine: AudioEngine,
  spec: CuratedSfxSpec | undefined,
  audioAssets: Record<string, PublishedAudioAsset> = {},
  fetchFn: FetchAudio = fetch,
) {
  const cues = new Map<string, string | null>();
  const controllers = new Set<AbortController>();
  const states = new Map<string, LoadState>();
  const loads = new Map<string, Promise<boolean>>();
  let disposed = false;
  let musicLoaded = false;
  const stats = (): AudioLoadStats => {
    let loaded = 0; let failed = 0; let pending = 0;
    for (const state of states.values()) { if (state === 'loaded') loaded++; else if (state === 'failed') failed++; else pending++; }
    return { loaded, failed, pending };
  };
  const fetchBounded = async (asset: PublishedAudioAsset, controller: AbortController): Promise<ArrayBuffer | null> => {
    const maxBytes = AUDIO_MEDIA_FORMATS[asset.kind].maxBytes;
    if (!Number.isSafeInteger(asset.audio.bytes) || asset.audio.bytes < 1 || asset.audio.bytes > maxBytes) return null;
    const timeout = setTimeout(() => controller.abort(), 10_000);
    try {
      const response = await fetchFn(asset.url, { signal: controller.signal, redirect: 'error' });
      if (!response.ok || !response.body) return null;
      const contentLength = response.headers.get('content-length');
      if (contentLength !== null && (!/^\d+$/.test(contentLength) || Number(contentLength) > asset.audio.bytes)) return null;
      const reader = response.body.getReader();
      // One bounded output buffer avoids retaining an attacker-controlled count
      // of tiny chunks alongside the final contiguous byte copy.
      const bytes = new Uint8Array(asset.audio.bytes);
      let total = 0;
      try {
        while (true) {
          const next = await reader.read();
          if (next.done) break;
          if (!next.value) continue;
          if (next.value.byteLength > asset.audio.bytes - total || next.value.byteLength > maxBytes - total) { await reader.cancel(); return null; }
          bytes.set(next.value, total);
          total += next.value.byteLength;
        }
      } finally { reader.releaseLock(); }
      if (total !== asset.audio.bytes) return null;
      return bytes.buffer;
    } finally {
      clearTimeout(timeout);
    }
  };
  const loadUploaded = (selection: Exclude<AudioSelection, 'none'>, expectedKind: AudioMediaKind): Promise<boolean> => {
    const asset = audioAssets[selection.variantId];
    if (!asset || asset.kind !== expectedKind || asset.contentSha256 !== selection.contentSha256 || asset.url !== relativeAudioUrl(asset)) {
      states.set(selection.variantId, 'failed'); return Promise.resolve(false);
    }
    const prior = loads.get(selection.variantId);
    if (prior) return prior;
    const controller = new AbortController(); controllers.add(controller); states.set(selection.variantId, 'pending');
    const load = (async (): Promise<boolean> => {
      try {
        const bytes = await fetchBounded(asset, controller);
        if (!bytes || disposed || controller.signal.aborted) return false;
        const loaded = await engine.loadVerifiedAudio(selection.variantId, bytes, selection.contentSha256, asset.kind, asset.audio);
        if (disposed || controller.signal.aborted) return false;
        if (loaded && asset.kind === 'music') musicLoaded = true;
        return loaded;
      } catch { return false; } finally { controllers.delete(controller); }
    })();
    loads.set(selection.variantId, load);
    void load.then(loaded => states.set(selection.variantId, loaded && !disposed ? 'loaded' : 'failed'));
    return load;
  };
  const loadCurated = (selection: Exclude<AudioSelection, 'none'>): Promise<boolean> => {
    const prior = loads.get(selection.variantId);
    if (prior) return prior;
    states.set(selection.variantId, 'pending');
    const load = engine.loadSample(selection.variantId, curatedImpactWav(), selection.contentSha256)
      .catch(() => false);
    loads.set(selection.variantId, load);
    void load.then(loaded => states.set(selection.variantId, loaded && !disposed ? 'loaded' : 'failed'));
    return load;
  };
  const loadBank = (selection: Exclude<AudioSelection, 'none'>): Promise<{ loaded: boolean; sampleId: string }> => {
    const entry = bankEntry(selection.variantId);
    const sampleId = entry ? bankSampleId(entry.id) : `bank/${selection.variantId}`;
    if (!entry || entry.contentSha256 !== selection.contentSha256) {
      states.set(sampleId, 'failed');
      return Promise.resolve({ loaded: false, sampleId });
    }
    const prior = loads.get(sampleId);
    if (prior) return prior.then(loaded => ({ loaded, sampleId }));
    states.set(sampleId, 'pending');
    const load = engine.loadSample(sampleId, renderBankEntry(entry), entry.contentSha256).catch(() => false);
    loads.set(sampleId, load);
    void load.then(loaded => states.set(sampleId, loaded && !disposed ? 'loaded' : 'failed'));
    return load.then(loaded => ({ loaded, sampleId }));
  };
  const readyLoads: Promise<boolean>[] = [];
  if (spec?.cues && typeof spec.cues === 'object' && !Array.isArray(spec.cues) && [1, 2, 3].includes(spec.schemaVersion)) {
    for (const [cue, binding] of Object.entries(spec.cues)) {
      if (cues.size >= 32) break;
      cues.set(cue, null);
      if (!isCueName(cue) || !isSelection(binding)) continue;
      if (binding.source === 'curated-impact-v1') {
        if (binding.variantId !== CURATED_IMPACT_V1.variantId || binding.contentSha256 !== CURATED_IMPACT_V1.contentSha256) continue;
        cues.set(cue, binding.variantId);
        readyLoads.push(loadCurated(binding));
      } else if (binding.source === 'bank-sfx-v1' && spec.schemaVersion === 3) {
        const entry = bankEntry(binding.variantId);
        if (!entry || entry.contentSha256 !== binding.contentSha256) continue;
        const sampleId = bankSampleId(entry.id);
        cues.set(cue, sampleId);
        readyLoads.push(loadBank(binding).then(result => result.loaded));
      } else if (spec.schemaVersion >= 2) {
        cues.set(cue, binding.variantId);
        readyLoads.push(loadUploaded(binding, 'sfx'));
      }
    }
    const music = spec.music;
    if (spec.schemaVersion >= 2 && isSelection(music) && music.source === 'uploaded-audio-v1') readyLoads.push(loadUploaded(music, 'music'));
  }
  return {
    ready: Promise.all(readyLoads).then(() => {
      if (musicLoaded && !disposed) engine.startMusic();
    }),
    get stats(): AudioLoadStats { return stats(); },
    /** A load may finish while gameplay is suspended; retry only when the
     * shell permits playback again. The engine retains the lifecycle gate. */
    startMusicIfReady(): void { if (musicLoaded && !disposed) engine.startMusic(); },
    play(cue: string): boolean {
      if (disposed) return cues.has(cue) || (!!spec?.cues && Object.hasOwn(spec.cues, cue));
      if (!cues.has(cue)) return !!spec?.cues && Object.hasOwn(spec.cues, cue);
      const id = cues.get(cue);
      if (id) engine.playSample(id);
      return true;
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      for (const controller of controllers) controller.abort();
      controllers.clear();
      // Release ONLY this version's selections: the bank binding shares the
      // engine and outlives the match. The engine's per-id request token
      // ensures an in-flight hash cannot revive a departed version's sample.
      engine.unloadSamples(loads.keys());
      engine.unloadMusic();
    },
  };
}
